export type DeviceType = 'tablet' | 'desktop';

/**
 * Минимальная ширина экрана (короткая сторона, CSS px), начиная с которой
 * устройство может считаться планшетом. Всё, что уже, — телефон.
 */
export const TABLET_MIN_SCREEN_WIDTH = 600;

export interface TabletDetectionHints {
  /** navigator.userAgentData?.mobile — доступно только в Chromium-браузерах. */
  uaDataMobile?: boolean;
  /** navigator.userAgentData?.platform — доступно только в Chromium-браузерах. */
  uaDataPlatform?: string;
  /** navigator.maxTouchPoints */
  maxTouchPoints?: number;
}

/**
 * Определяет планшет по user agent и ширине экрана.
 *
 * - iPad — всегда планшет (в том числе iPad mini с шириной 744 px и iPadOS,
 *   который в Safari представляется как macOS, но имеет сенсорный экран);
 * - экран уже TABLET_MIN_SCREEN_WIDTH — телефон, не планшет;
 * - Android — планшет, только если в user agent нет `Mobile`
 *   (стандартный признак телефона);
 * - иначе — по Client Hints: `userAgentData.mobile === false` на Android
 *   с сенсорным экраном. Платформа проверяется, чтобы ноутбуки
 *   с сенсорным экраном не получали планшетный интерфейс.
 *
 * @param ua user agent
 * @param width ширина экрана в CSS px (короткая сторона, чтобы не зависеть от ориентации)
 * @param hints дополнительные признаки из navigator
 */
export const isTabletUserAgent = (
  ua: string,
  width: number,
  hints: TabletDetectionHints = {},
): boolean => {
  const { uaDataMobile, uaDataPlatform, maxTouchPoints = 0 } = hints;

  if (/ipad/i.test(ua) || (/macintosh/i.test(ua) && maxTouchPoints > 1)) {
    return true;
  }

  if (width < TABLET_MIN_SCREEN_WIDTH) {
    return false;
  }

  if (/android/i.test(ua)) {
    return !/mobile/i.test(ua);
  }

  if (/tablet/i.test(ua)) {
    return true;
  }

  return (
    !uaDataMobile &&
    (!uaDataPlatform || uaDataPlatform === 'Android') &&
    maxTouchPoints > 0
  );
};

type NavigatorWithUAData = Navigator & {
  userAgentData?: { mobile?: boolean; platform?: string };
};

export const detectDevice = (): DeviceType => {
  const nav = navigator as NavigatorWithUAData;
  const screenWidth = Math.min(window.screen.width, window.screen.height);

  return isTabletUserAgent(nav.userAgent, screenWidth, {
    uaDataMobile: nav.userAgentData?.mobile,
    uaDataPlatform: nav.userAgentData?.platform,
    maxTouchPoints: nav.maxTouchPoints,
  })
    ? 'tablet'
    : 'desktop';
};
